import {UseQueryResult, useQuery} from "@tanstack/react-query";
import {api} from "@/features/api";

export type UserAddress = {
  name: string;
  address: string;
  city: string;
  postCode: string;
  countryCode: string;
};

export type UserFlag =
  | "pendingDeletion"
  | "establishedUser"
  | "verifiedAge"
  | "phoneVerificationIssue";

export type UserLanguage = "formal" | "casual";

export type UserSex = "male" | "female";

export type UserWage =
  | "doctors"
  | "children"
  | "shelter"
  | "trendaro"
  | "money";

// https://behavio-api-docs.web.app/trendaro-admin-v2-client.html#tag/Profile/operation/GetProfile
export type UserProfile = {
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string;
  sex?: UserSex;
  language?: UserLanguage | null;
  wage?: UserWage | null;
  phoneNumber?: string;
  bankAccount?: string;
  contactAddress?: UserAddress;
  shippingAddress?: UserAddress;
  affiliateCode?: string;
  notifyPollPig?: boolean;
  notifyPollRabbit?: boolean;
  homeTesting?: string[];
  flags?: UserFlag[];
  state?:
    | "active"
    | "zombie"
    | "deleted"
    | "sleeping"
    | "pending_deletion"
    | "pending_email_verification";

  // this state is optional, because it is not used in PUT requests
  phoneVerificationState?:
    | "not_verified"
    | "verified"
    | "wrong_answer"
    | "call_again"
    | "call_interrupted"
    | "refused_verification"
    | "wrong_number"
    | "no_pickup1"
    | "no_pickup2"
    | "no_pickup3"
    | null;
};

export const isPendingDeletion = (flags: UserFlag[] = []) =>
  flags.includes("pendingDeletion");
export const isEstablishedUser = (flags: UserFlag[] = []) =>
  flags.includes("establishedUser");
export const isVerifiedAge = (flags: UserFlag[] = []) =>
  flags.includes("verifiedAge");
export const isPhoneVerificationIssue = (flags: UserFlag[] = []) =>
  flags.includes("phoneVerificationIssue");

type ResponseType = UserProfile;

const useGetUserProfile: (
  userId: string | undefined,
) => UseQueryResult<ResponseType, Error> = (userId) => {
  return useQuery({
    queryKey: ["getUserProfile", userId],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await api("TRENDARO_API_URL").get(`/users/me/profile`);

      // catch a error if the response status is 401
      // https://tanstack.com/query/latest/docs/framework/react/guides/query-functions?from=reactQueryV3#usage-with-fetch-and-other-clients-that-do-not-throw-by-default
      if (response.status === 401)
        throw new Error(
          "Neoprávněný přístup k Trendaro API při načítání uživatelského profilu.",
        );

      if (!response.ok)
        throw new Error(
          `HTTP ${response.status} při načítání uživatelského profilu z Trendaro API.`,
        );

      // catch a error if the response does not contain valid JSON
      try {
        const json = (await response.json()) as ResponseType;

        return json;
      } catch {
        throw new Error(
          "Data z Trendaro API nejsou ve správném formátu při načítání uživatelského profilu.",
        );
      }
    },
    enabled: !!userId,
  });
};

export {useGetUserProfile};
